import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  IconButton,
  Tooltip,
  Alert,
  Chip,
  Divider,
  Paper,
  Grid,
  InputAdornment,
  Snackbar
} from '@mui/material';
import {
  Close,
  ContentCopy,
  Visibility,
  VisibilityOff,
  Info,
  Warning
} from '@mui/icons-material';

interface OIDCCredentialsDialogProps {
  open: boolean;
  onClose: () => void;
  credentials: {
    clientId: string;
    clientSecret?: string;
    oidcDiscoveryEndpoint: string;
    clientSecretExpiresAt?: string;
    grantType: 'authorization_code' | 'client_credentials' | 'authorization_code_pkce';
    connectionName: string;
  };
}

const OIDCCredentialsDialog: React.FC<OIDCCredentialsDialogProps> = ({
  open,
  onClose,
  credentials
}) => {
  const [showSecret, setShowSecret] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const handleCopy = async (text: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
    setCopiedField(null);
  };

  const formatExpirationDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid Date';
    }
  };

  const isSecretExpiringSoon = (dateString?: string) => {
    if (!dateString) return false;
    try {
      const expirationDate = new Date(dateString);
      const thirtyDaysFromNow = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
      return expirationDate <= thirtyDaysFromNow;
    } catch {
      return false;
    }
  };

  const getGrantTypeDisplayName = (grantType: string) => {
    switch (grantType) {
      case 'authorization_code_pkce':
        return 'Authorization Code with PKCE';
      case 'authorization_code':
        return 'Authorization Code';
      case 'client_credentials':
        return 'Client Credentials';
      default:
        return grantType;
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">
              OIDC Configuration Complete - {credentials.connectionName}
            </Typography>
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Success!</strong> Your OIDC application has been configured in Azure AD. 
                Use the credentials below to integrate with your application.
              </Typography>
            </Alert>

            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                <Info sx={{ mr: 1, verticalAlign: 'middle' }} />
                Configuration Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Grant Type:
                  </Typography>
                  <Chip 
                    label={getGrantTypeDisplayName(credentials.grantType)} 
                    color="primary" 
                    size="small" 
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Connection Name:
                  </Typography>
                  <Typography variant="body2">{credentials.connectionName}</Typography>
                </Grid>
              </Grid>
            </Paper>
          </Box>

          <Grid container spacing={3}>
            {/* Client ID */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Client ID <Chip label="Required" size="small" color="primary" />
              </Typography>
              <TextField
                fullWidth
                value={credentials.clientId}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Copy Client ID">
                        <IconButton
                          onClick={() => handleCopy(credentials.clientId, 'clientId')}
                          edge="end"
                        >
                          <ContentCopy />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                variant="outlined"
                size="small"
              />
            </Grid>

            {/* Client Secret (if applicable) */}
            {credentials.clientSecret && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Client Secret <Chip label="Required" size="small" color="primary" />
                </Typography>
                <TextField
                  fullWidth
                  type={showSecret ? 'text' : 'password'}
                  value={credentials.clientSecret}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title={showSecret ? 'Hide Secret' : 'Show Secret'}>
                          <IconButton
                            onClick={() => setShowSecret(!showSecret)}
                            edge="end"
                          >
                            {showSecret ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Copy Secret">
                          <IconButton
                            onClick={() => handleCopy(credentials.clientSecret!, 'clientSecret')}
                            edge="end"
                          >
                            <ContentCopy />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  variant="outlined"
                  size="small"
                />
                {credentials.clientSecretExpiresAt && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Expires: {formatExpirationDate(credentials.clientSecretExpiresAt)}
                    </Typography>
                    {isSecretExpiringSoon(credentials.clientSecretExpiresAt) && (
                      <Alert severity="warning" sx={{ mt: 1 }}>
                        <Warning sx={{ mr: 1, verticalAlign: 'middle' }} />
                        This secret expires soon! Please generate a new one before the expiration date.
                      </Alert>
                    )}
                  </Box>
                )}
              </Grid>
            )}

            {/* PKCE Info */}
            {credentials.grantType === 'authorization_code_pkce' && (
              <Grid item xs={12}>
                <Alert severity="info">
                  <Typography variant="body2">
                    <strong>PKCE Flow:</strong> This application uses PKCE (Proof Key for Code Exchange) 
                    for enhanced security. No client secret is required. Use the Client ID above with 
                    PKCE parameters in your authorization flow.
                  </Typography>
                </Alert>
              </Grid>
            )}

            {/* OIDC Discovery Endpoint */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                OIDC Discovery Endpoint <Chip label="Required" size="small" color="primary" />
              </Typography>
              <TextField
                fullWidth
                value={credentials.oidcDiscoveryEndpoint}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Copy Discovery Endpoint">
                        <IconButton
                          onClick={() => handleCopy(credentials.oidcDiscoveryEndpoint, 'discoveryEndpoint')}
                          edge="end"
                        >
                          <ContentCopy />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                variant="outlined"
                size="small"
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Use this endpoint to discover OIDC configuration and endpoints
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              <Info sx={{ mr: 1, verticalAlign: 'middle' }} />
              Next Steps
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              1. Configure your application to use the Client ID and Discovery Endpoint above
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              2. {credentials.clientSecret ? 'Include the Client Secret in your application configuration' : 'Use PKCE parameters in your authorization flow (no secret required)'}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              3. Test the integration to ensure everything works correctly
            </Typography>
            {credentials.clientSecretExpiresAt && (
              <Typography variant="body2" color="text.secondary">
                4. Set a reminder to renew the client secret before {formatExpirationDate(credentials.clientSecretExpiresAt)}
              </Typography>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} variant="contained">
            Got it!
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={handleCloseSnackbar}
        message={`${copiedField === 'clientId' ? 'Client ID' : 
                  copiedField === 'clientSecret' ? 'Client Secret' : 
                  'Discovery Endpoint'} copied to clipboard!`}
      />
    </>
  );
};

export default OIDCCredentialsDialog;
